import { signal, WritableSignal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MultiSelectFilterOption, SortOrder } from '@shs-digi-care-monorepo/frontend-lib';
import { AuxiliaryRequestState, Flags, ITeam, RequestState, State } from '@shs-digi-care-monorepo/interfaces';

export type StatusFilterValue = RequestState | AuxiliaryRequestState;

/**
 * Encapsulates all filter, search, sorting, and pagination state for a single tab.
 * Each tab gets its own instance so switching tabs preserves independent state.
 */
export class TabFilterState {
    // Search
    public readonly searchControl = new FormControl('');
    public readonly searchTermValue: WritableSignal<string | undefined> = signal(undefined);

    // Filters
    public readonly statusFilterValue: WritableSignal<StatusFilterValue[]>;
    public readonly flagsFilterValue: WritableSignal<Flags[]> = signal([]);
    public readonly teamFilterValue: WritableSignal<ITeam[]> = signal([]);
    public readonly requestorFilterValue: WritableSignal<string[]> = signal([]);
    public readonly requestorEmailFilterValue: WritableSignal<string[]> = signal([]);
    public readonly stateFilterValue: WritableSignal<State[]> = signal([]);
    public readonly assigneeFilterValue: WritableSignal<string[]> = signal([]);

    // Toggle filters
    public readonly onlyMyAssignedTasks: WritableSignal<boolean> = signal(false);
    public readonly globalSearchActive: WritableSignal<boolean> = signal(false);

    // Snapshot of filters before global search was activated, used to restore on deactivation
    public savedFilterSnapshot: {
        statusFilterValue: StatusFilterValue[];
        flagsFilterValue: Flags[];
        teamFilterValue: ITeam[];
        requestorFilterValue: string[];
        requestorEmailFilterValue: string[];
        stateFilterValue: State[];
        assigneeFilterValue: string[];
        onlyMyAssignedTasks: boolean;
    } | null = null;

    // Dynamic filter options (loaded from API)
    public readonly requestorEmailOptions: WritableSignal<MultiSelectFilterOption<string>[]> = signal([]);
    public readonly assigneeOptions: WritableSignal<MultiSelectFilterOption<string>[]> = signal([]);

    // Pagination
    public readonly page: WritableSignal<number> = signal(1);
    public readonly pageSize: WritableSignal<number>;

    // Sorting
    public readonly orderBy: WritableSignal<string[]>;
    public readonly sortOrder: WritableSignal<SortOrder>;

    constructor(
        defaultStatus: StatusFilterValue[] = [],
        defaultOrderBy: string[] = ['submitted_date'],
        defaultSortOrder: SortOrder = SortOrder.DESC,
        defaultPageSize = 10
    ) {
        this.statusFilterValue = signal(defaultStatus);
        this.orderBy = signal(defaultOrderBy);
        this.sortOrder = signal(defaultSortOrder);
        this.pageSize = signal(defaultPageSize);
    }
}
